import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Button, Card, CardBody, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, Row, UncontrolledDropdown } from 'reactstrap'
import projects from '../../../api/projects/hooks'
import { MODAL_TYPES } from '../../../common/constants'
import AppPermissionsSwitcher from '../../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../../Components/AppPermissionsSwitcher/constants/actions'
import PageTitle from '../../../Components/Common/PageTitle'
import { setupDeleteCallback } from '../../../Components/Modals/DeleteModal/utils'
import QueryErrorBoundary from '../../../Components/QueryErrorBoundary/QueryErrorBoundary'
import { DataTableLayout, useDataTable, useRowClick, useTableState } from '../../../Components/TanstackTable'
import { showModal } from '../../../store/modal'
import { selectSearchCriteriaProjects } from '../../../store/search'
import { selectCheckedTableRows } from '../../../store/table'
import { useTableColumns } from '../hooks'
import { getFilteredProject } from '../utils'
import { getModalPayloads } from './listModalPayloads'

const ProjectList = () => {
    const history = useHistory()
    const { t } = useTranslation()

    const projectSearchCriteria = useSelector(selectSearchCriteriaProjects)

    const tableState = useTableState({
        tableKey: 'project',
        initialPage: history.location.state?.page,
    })

    const queryParams = { ...tableState.params, criteria: projectSearchCriteria }

    return (
        <div className="page-content">
            <Container fluid>
                <PageTitle title={t('app.common.projects')} />
                <Row>
                    <Col lg={12}>
                        <Card id="ProjectList">
                            <CardBody>
                                <QueryErrorBoundary resetKeys={[JSON.stringify(queryParams)]}>
                                    <ProjectTable tableState={tableState} queryParams={queryParams} />
                                </QueryErrorBoundary>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

const ProjectTable = ({ tableState, queryParams }) => {
    const location = useLocation()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { DELETE_MODAL, CHANGE_AGENT_MODAL, OFFER_BASE_MODAL, EXPORT_DATA_MODAL } = MODAL_TYPES

    const selectedTableRows = useSelector(selectCheckedTableRows)

    const getProjectsConfig = {
        params: queryParams,
        isEnabled: { enabled: Boolean(tableState.params.page && tableState.params.limit), throwOnError: true },
    }

    const { data: projectsData, isLoading } = projects.GetProjects.useQuery(getProjectsConfig.params, getProjectsConfig.isEnabled)
    const { mutate: deleteProjects } = projects.DeleteProjects.useMutation()

    const projectItems = useMemo(() => projectsData?.items ?? [], [projectsData?.items])
    const totalPages = projectsData?.pagination?.totalPages

    const onRowClick = useRowClick('/projects')
    const columns = useTableColumns('projects')

    const { table, updateColumnSizing } = useDataTable({
        ...tableState,
        columns,
        data: projectItems,
        pageCount: totalPages,
        selectRow: { mapRow: getFilteredProject },
    })

    const handleModal = (modalType) => {
        const payload = getModalPayloads({
            modalType,
            selectedTableRows,
            params: queryParams,
            columns,
        })

        if (!payload) return

        setupDeleteCallback(modalType, payload.modalProps, (ids) => deleteProjects({ ids }))
        dispatch(showModal(payload))
    }

    const tableActions = () => (
        <div className="d-flex flex-grow-1">
            <Button tag={Link} to="/projects/new" color="add" className="btn-label">
                <i className="mdi mdi-layers-outline align-bottom label-icon align-middle fs-16 me-2"></i>
                {t('button.add')}
            </Button>
            <UncontrolledDropdown className="ms-2">
                <DropdownToggle title={t('table.showMore')} href="#" className="btn btn-primary dropdown" tag="button">
                    <i className="ri-more-fill align-middle"></i>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                    <DropdownItem href="#" onClick={() => handleModal(DELETE_MODAL)} disabled={!selectedTableRows.length}>
                        <i className="mdi mdi-trash-can-outline align-bottom me-2 text-muted"></i> {t('table.action.deleteSelected')}
                    </DropdownItem>
                    <AppPermissionsSwitcher permission="ChangeAgent">
                        {({ hasPermission }) =>
                            hasPermission && (
                                <DropdownItem
                                    onClick={() => handleModal(CHANGE_AGENT_MODAL)}
                                    disabled={!selectedTableRows.length}
                                >
                                    <i className="mdi mdi-account-switch-outline align-bottom me-2 text-muted"></i> {t('app.common.switchAgent')}
                                </DropdownItem>
                            )
                        }
                    </AppPermissionsSwitcher>
                    <DropdownItem onClick={() => handleModal(OFFER_BASE_MODAL)} disabled={!selectedTableRows.length}>
                        <i className="mdi mdi-file-document-multiple-outline align-bottom me-2 text-muted"></i> {t('app.common.offerOrPrint')}
                    </DropdownItem>
                    <AppPermissionsSwitcher permission="ExportData">
                        {({ hasPermission }) =>
                            hasPermission && (
                                <DropdownItem onClick={() => handleModal(EXPORT_DATA_MODAL)}>
                                    <i className="mdi mdi-export align-bottom me-2 text-muted"></i> {t('app.common.exportData.title')}
                                </DropdownItem>
                            )
                        }
                    </AppPermissionsSwitcher>
                </DropdownMenu>
            </UncontrolledDropdown>
        </div>
    )

    const handlePermissions = ({ row, children }) => {
        const agent = row.original.agent
        const itemAgentId = agent?.[0]?.id
        const itemAgentOfficeId = agent?.[0]?.agencyOffice?.id

        return (
            <AppPermissionsSwitcher
                key={row.id}
                permission="ModulePermissions"
                itemAgentId={itemAgentId}
                itemAgentOfficeId={itemAgentOfficeId}
                module={location}
                action={action.view}
            >
                {({ hasModulePermission }) =>
                    children({
                        onClick: (e) => hasModulePermission && onRowClick(row.original.id, e, tableState.params.page),
                        style: { cursor: hasModulePermission ? 'pointer' : 'not-allowed' },
                    })
                }
            </AppPermissionsSwitcher>
        )
    }

    return (
        <DataTableLayout
            table={table}
            isLoading={isLoading}
            totalItems={projectsData?.pagination?.totalItems ?? 0}
            hasColumnSelection
            hasPageSizeSelect
            theadClass="dmn-table-header"
            toolbarActions={tableActions}
            updateColumnSizing={updateColumnSizing}
            renderRow={handlePermissions}
        />
    )
}

export default ProjectList
